/**
 * Public app origin for auth redirects, invite links, email links, and
 * Stripe return URLs.
 *
 * Server code, including Netlify functions, reads the APP_BASE_URL secret.
 * Outside production, an APP_BASE_URL of https://app.pacarion.com does not
 * override SITE_URL or a develop branch deploy.
 * There are no Supabase edge functions in this repo. A future edge function
 * should read the same secret. When APP_BASE_URL is unset, production uses
 * https://app.pacarion.com. https://pacarion.com and www.pacarion.com are the
 * marketing site and are not used as that fallback.
 *
 * The browser reads REACT_APP_APP_URL. When that is unset, an http(s) page
 * uses window.location.origin, except the marketing hosts, which use the app
 * origin. A capacitor origin falls back to the server base so a native shell
 * does not email capacitor://localhost.
 *
 * Netlify functions only see URL at runtime, not DEPLOY_PRIME_URL. A branch
 * deploy therefore sets SITE_URL (Branch deploys context only, never Production).
 * A Netlify URL of https://pacarion.com is ignored and falls back to the app origin.
 * See docs/ENVIRONMENTS.md.
 */

import { Capacitor } from '@capacitor/core';

const MARKETING_SITE = 'https://pacarion.com';
const PRODUCTION_SITE = 'https://app.pacarion.com';
const DEVELOP_SITE = 'https://develop--pacarion.netlify.app';
const MARKETING_HOSTS = new Set(['pacarion.com', 'www.pacarion.com']);

function trimOrigin(value) {
  return String(value || '').trim().replace(/\/$/, '');
}

function isMarketingOrigin(value) {
  const raw = trimOrigin(value);
  if (!raw) return false;
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' && MARKETING_HOSTS.has(url.hostname.toLowerCase());
  } catch (err) {
    return false;
  }
}

function deployOrigin(value) {
  const raw = trimOrigin(value);
  if (!raw || isMarketingOrigin(raw)) return '';
  return raw;
}

function isProductionApp(value) {
  return trimOrigin(value) === PRODUCTION_SITE;
}

/**
 * Production uses APP_BASE_URL, then https://app.pacarion.com.
 * Everywhere else, a copied live APP_BASE_URL does not win. SITE_URL,
 * DEPLOY_PRIME_URL, and DEPLOY_URL are the environment's own host. A develop
 * branch deploy with none of those still uses https://develop--pacarion.netlify.app.
 * A non-live APP_BASE_URL still overrides those hosts.
 */
function siteBase(env = process.env) {
  const context = String((env && env.CONTEXT) || '');
  const configured = trimOrigin(env && env.APP_BASE_URL);
  if (context === 'production') return configured || PRODUCTION_SITE;
  const copiedLive = Boolean(configured && isProductionApp(configured));
  if (configured && !copiedLive) return configured;
  const site = deployOrigin(env && env.SITE_URL);
  if (site) return site;
  const prime = deployOrigin(env && env.DEPLOY_PRIME_URL);
  if (prime) return prime;
  const deploy = deployOrigin(env && env.DEPLOY_URL);
  if (deploy) return deploy;
  const branch = String((env && env.BRANCH) || '').toLowerCase();
  if (branch === 'develop' || context === 'branch-deploy') return DEVELOP_SITE;
  const url = deployOrigin(env && env.URL);
  if (url && !isProductionApp(url)) return url;
  if (configured) return configured;
  return PRODUCTION_SITE;
}

/**
 * Frontend app origin. REACT_APP_APP_URL wins. Otherwise an http(s) page
 * origin, including window.location.origin, is used. The marketing hosts
 * resolve to the app origin.
 */
function browserAppBase(location, env = process.env) {
  const configured = trimOrigin(env && env.REACT_APP_APP_URL);
  if (configured) return configured;
  const loc = location || (typeof window !== 'undefined' ? window.location : null);
  const origin = trimOrigin(loc && loc.origin);
  const protocol = String((loc && loc.protocol) || '').toLowerCase();
  if (origin && origin !== 'null' && (protocol === 'http:' || protocol === 'https:')) {
    if (isMarketingOrigin(origin)) return PRODUCTION_SITE;
    return origin;
  }
  return siteBase(env);
}

function authConfirmUrl(location, env = process.env) {
  return `${browserAppBase(location, env)}/auth/confirm`;
}

function appPathUrl(suffix, env = process.env) {
  const base = siteBase(env);
  const extra = String(suffix || '');
  if (!extra) return base;
  if (extra.startsWith('/') || extra.startsWith('?') || extra.startsWith('#')) return `${base}${extra}`;
  return `${base}/${extra}`;
}

function isLocalOrCapacitorOrigin(location) {
  const host = String((location && location.hostname) || '').replace(/^\[|\]$/g, '').toLowerCase();
  const protocol = String((location && location.protocol) || '').toLowerCase();
  const origin = String((location && location.origin) || '').toLowerCase();
  if (protocol === 'capacitor:' || origin.startsWith('capacitor://')) return true;
  return host === 'localhost' || host === '127.0.0.1' || host === '::1';
}

function runningInNativeApp() {
  try {
    return Boolean(Capacitor && typeof Capacitor.isNativePlatform === 'function' && Capacitor.isNativePlatform());
  } catch (err) {
    return false;
  }
}

/**
 * Browser copy of an invite link. REACT_APP_APP_URL wins. The native shells
 * and localhost otherwise use the server base (https://app.pacarion.com until
 * APP_BASE_URL is set). A hosted deploy keeps its own origin. The marketing
 * hosts resolve to the app origin.
 * `native` overrides Capacitor.isNativePlatform() in tests.
 */
function siteBaseFromLocation(location, env = process.env, native) {
  const configured = trimOrigin(env && env.REACT_APP_APP_URL);
  if (configured) return configured;
  const onDevice = native === undefined ? runningInNativeApp() : Boolean(native);
  if (onDevice || isLocalOrCapacitorOrigin(location)) {
    return trimOrigin(env && env.APP_BASE_URL) || PRODUCTION_SITE;
  }
  const host = String((location && location.hostname) || '').toLowerCase();
  if (MARKETING_HOSTS.has(host)) return PRODUCTION_SITE;
  const origin = trimOrigin(location && location.origin);
  if (origin && origin !== 'null' && host) return origin;
  return siteBase(env);
}

function apiV1Url(path, location, env = process.env, native) {
  const suffix = String(path || '').replace(/^\//, '');
  return `${siteBaseFromLocation(location, env, native)}/api/v1/${suffix}`;
}

export {
  MARKETING_SITE,
  PRODUCTION_SITE,
  DEVELOP_SITE,
  isMarketingOrigin,
  siteBase,
  siteBaseFromLocation,
  browserAppBase,
  authConfirmUrl,
  appPathUrl,
  apiV1Url,
};
